import { AxiosError } from 'axios';
import { PaginationDataModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { PaginationUtil } from '../../../../shared/components/pagination/utils/PaginationUtils';
import { left, right } from '../../../../shared/core/Either';
import { Result } from '../../../../shared/core/Result';
import { APIResponse } from '../../../../shared/infra/api/APIResponse';
import { BaseAPI } from '../../../../shared/infra/api/BaseAPI';
import { IAuthService } from '../../../users/services/authService';
import { AnalyticPercentageResponseDTO } from '../dtos/AnalyticPercentage.dto';
import { CommunitiesOverviewResponseDTO } from '../dtos/Communities.dto';
import { CommunityFilteredAnalyticsResponseDTO } from '../dtos/CommunityFilteredAnalytics.dto';
import { FilteredAnalyticsResponseDTO } from '../dtos/FilteredAnalytics.dto';
import { AnalyticOverviewResponseDTO } from '../dtos/Overview.dto';
import {
  AdminAnalyticsTypes,
  DataPoint,
} from '../infra/types/AnalyticsOverviewTypes';
import { AnalyticPercentageModel } from '../models/AnalyticPercentage.model';
import { CommunitiesOverviewModel } from '../models/CommunitiesOverview.model';
import { CommunityFilteredAnalyticsModel } from '../models/CommunityFilteredAnalytics.model';
import { FilteredAnalyticsModel } from '../models/FilteredAnalytics.model';
import { AnalyticPercentageUtil } from '../utils/AnalyticPercentageUtil';
import { AnalyticsOverviewUtil } from '../utils/AnalyticsOverviewUtil';
import { CommunitiesOverviewUtil } from '../utils/CommunitiesOverviewUtil';
import { CommunityFilteredAnalyticsUtil } from '../utils/CommunityFilteredAnalyticsUtil';
import { FilteredAnalyticsUtil } from '../utils/FilteredAnalyticsUtil';
import { OpenAnalyticOverviewResponseDTO } from '../dtos/OpenOverview.dto';
import { OpenAnalyticsOverviewUtil } from '../utils/OpenAnalyticsOverviewUtil';

export interface IAdminAnalyticsService {
  getCommunitesOverview(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<CommunitiesOverviewModel[]>>>;
  getFilteredAnalytics(
    filter: AdminAnalyticsTypes,
    range?: Date[],
  ): Promise<APIResponse<DataPoint[]>>;
  getOpenFilteredAnalytics(
    filter: AdminAnalyticsTypes,
    range?: Date[],
  ): Promise<APIResponse<DataPoint[]>>;
  getAnalyticsOverview(
    filter: AdminAnalyticsTypes,
    range?: Date[],
  ): Promise<APIResponse<DataPoint[]>>;
  getCommunityPercentages(
    filter: AdminAnalyticsTypes,
    range?: Date[],
  ): Promise<APIResponse<AnalyticPercentageModel>>;
  getCommunityFilteredAnalytics(
    filter: AdminAnalyticsTypes,
    range?: Date[] | undefined,
    offset?: number,
    dir?: number,
    search?: string,
  ): Promise<
    APIResponse<PaginationDataModel<CommunityFilteredAnalyticsModel[]>>
  >;
  getMultilineFilteredAnalytics(
    filter: AdminAnalyticsTypes,
    range?: Date[],
  ): Promise<APIResponse<FilteredAnalyticsModel[]>>;
  getReport(range: Date[], email: string): Promise<APIResponse<any>>;
}

export class AdminAnalyticsService
  extends BaseAPI
  implements IAdminAnalyticsService
{
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error('Method not implemented.');
  }

  constructor(authService: IAuthService) {
    super(authService);
  }

  hasToken(): boolean {
    return this.authService.isUserAuthenticated();
  }

  async getReport(range: Date[], email: string): Promise<APIResponse<void>> {
    try {
      await this.post<any>({
        url: '/v2/reports/administrator/create',
        requiresToken: true,
        payload: {
          range,
          email,
        },
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Error');
    }
  }

  async getCommunityFilteredAnalytics(
    filter: AdminAnalyticsTypes,
    range?: Date[] | undefined,
    offset?: number,
    dir?: number,
    search?: string,
    orderKey?: string,
  ): Promise<
    APIResponse<PaginationDataModel<CommunityFilteredAnalyticsModel[]>>
  > {
    try {
      const response = await this.get<CommunityFilteredAnalyticsResponseDTO>({
        url: '/v1/administrator/analytics/main/filtered-communities',
        requiresToken: true,
        config: {
          params: {
            filter,
            range,
            offset,
            dir,
            search,
            orderKey,
          },
        },
      });

      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<CommunityFilteredAnalyticsModel[]>>({
          model: data.map((item) =>
            CommunityFilteredAnalyticsUtil.mapToViewModel(item),
          ),
          meta: PaginationUtil.mapToViewModel(
            meta,
            data[data.length - 1].communityData.orderKey,
          ),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Error');
    }
  }

  async getMultilineFilteredAnalytics(
    filter: AdminAnalyticsTypes,
    range?: Date[] | undefined,
  ): Promise<APIResponse<FilteredAnalyticsModel[]>> {
    try {
      const response = await this.get<FilteredAnalyticsResponseDTO>({
        url: '/v1/administrator/analytics/main/filtered-analytics',
        requiresToken: true,
        config: {
          params: {
            filter,
            range,
          },
        },
      });

      const { data } = response.data;

      return right(
        Result.ok<FilteredAnalyticsModel[]>(
          FilteredAnalyticsUtil.mapToViewModel(data),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Error');
    }
  }

  async getFilteredAnalytics(
    filter: AdminAnalyticsTypes,
    range?: Date[],
  ): Promise<APIResponse<DataPoint[]>> {
    try {
      const response = await this.get<AnalyticOverviewResponseDTO>({
        url: '/v1/administrator/analytics/main/filtered-overview',
        requiresToken: true,
        config: {
          params: {
            filter,
            range,
          },
        },
      });

      const { data } = response.data;

      return right(
        Result.ok<DataPoint[]>(AnalyticsOverviewUtil.mapToViewModel(data)),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  async getOpenFilteredAnalytics(
    filter: AdminAnalyticsTypes,
    range?: Date[] | undefined,
  ): Promise<APIResponse<DataPoint[]>> {
    try {
      const response = await this.get<OpenAnalyticOverviewResponseDTO>({
        url: '/v2/guests/admin/filtered-overview',
        requiresToken: true,
        config: {
          params: {
            filter,
            range,
          },
        },
      });

      const { data } = response.data;

      return right(
        Result.ok<DataPoint[]>(OpenAnalyticsOverviewUtil.mapToViewModel(data)),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  async getCommunityPercentages(
    filter: AdminAnalyticsTypes,
    range?: Date[],
  ): Promise<APIResponse<AnalyticPercentageModel>> {
    try {
      const response = await this.get<AnalyticPercentageResponseDTO>({
        url: '/v1/administrator/analytics/main/community-percentages',
        requiresToken: true,
        config: {
          params: {
            filter,
            range,
          },
        },
      });

      const { data } = response;

      return right(
        Result.ok<AnalyticPercentageModel>(
          AnalyticPercentageUtil.mapToViewModel(data),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  async getAnalyticsOverview(
    filter: AdminAnalyticsTypes,
    range?: Date[],
  ): Promise<APIResponse<DataPoint[]>> {
    let url: string;
    try {
      switch (filter) {
        case AdminAnalyticsTypes.ALL:
          url = '/v1/administrator/analytics/overview/all';
          break;
        case AdminAnalyticsTypes.MONTH:
          url = '/v1/administrator/analytics/overview/month';
          break;
        case AdminAnalyticsTypes.WEEK:
          url = '/v1/administrator/analytics/overview/week';
          break;
        default:
          throw new Error('Invalid Analytics Type');
      }

      const response = await this.get<AnalyticOverviewResponseDTO>({
        url: url,
        requiresToken: true,
        config: {
          params: {
            filter,
            range,
          },
        },
      });

      const { data } = response.data;

      return right(
        Result.ok<DataPoint[]>(AnalyticsOverviewUtil.mapToViewModel(data)),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  async getCommunitesOverview(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<CommunitiesOverviewModel[]>>> {
    try {
      const response = await this.get<CommunitiesOverviewResponseDTO>({
        url: '/v1/administrator/analytics/community/overview',
        requiresToken: true,
        config: {
          params: {
            offset,
            filter,
            dir,
            search,
          },
        },
      });

      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<CommunitiesOverviewModel[]>>({
          model: data.map((dto) => CommunitiesOverviewUtil.mapToViewModel(dto)),
          meta: PaginationUtil.mapToViewModel(meta),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }
}
